import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { Section, Spinner } from "../components/Bits";
import NewsletterForm from "../components/NewsletterForm";
import { TourBadges, GoodToKnow, BookingModal, TourDateRow } from "../components/TourBits";
import { useSiteText } from "../lib/siteTexts";
import { ChevronLeft, Leaf, Share2, Link2 } from "lucide-react";
import { toast } from "sonner";

function eventJsonLd(tour) {
  const origin = window.location.origin;
  return (tour.dates || []).map((d) => ({
    "@context": "https://schema.org",
    "@type": "Event",
    name: tour.title,
    description: tour.short_description,
    startDate: d.date,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    location: { "@type": "Place", name: d.meeting_point, address: d.meeting_point },
    image: tour.image_url ? [origin + tour.image_url] : undefined,
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: 0, priceCurrency: "EUR", availability: "https://schema.org/InStock", url: `${origin}/tours/${tour.slug}` },
    organizer: { "@type": "Organization", name: "AdventureCouple.lu", url: "https://adventurecouple.lu" },
    inLanguage: ["lb", "de"],
    audience: { "@type": "PeopleAudience", suggestedMinAge: 18 },
  }));
}

function ShareTour({ tour }) {
  const shareUrl = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/share/tours/${tour.slug}`;
  const text = `${tour.title} – kostenlose Naturwanderung mit AdventureCouple.lu`;
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const native = () => navigator.share({ title: tour.title, text, url: shareUrl }).catch(() => {});
  const copy = () => navigator.clipboard?.writeText(shareUrl).then(() => toast.success("Link kopiert"));
  const btn = "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors hover:bg-[var(--wp-cream)]";
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="tour-share">
      <span className="text-xs uppercase tracking-wider opacity-60 mr-1">Teilen</span>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${text} ${shareUrl}`)}`} target="_blank" rel="noopener noreferrer" className={btn} style={{ borderColor: "var(--wp-border)" }} data-testid="tour-share-whatsapp">
        <i className="fa-brands fa-whatsapp text-base" style={{ color: "#25D366" }} /> WhatsApp
      </a>
      {canShare ? (
        <button onClick={native} className={btn} style={{ borderColor: "var(--wp-border)" }} data-testid="tour-share-native">
          <Share2 className="w-4 h-4" /> Instagram & mehr
        </button>
      ) : (
        <button onClick={copy} className={btn} style={{ borderColor: "var(--wp-border)" }} data-testid="tour-share-copy">
          <Link2 className="w-4 h-4" /> Link kopieren
        </button>
      )}
    </div>
  );
}

export default function TourDetail() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [error, setError] = useState(false);
  const [booking, setBooking] = useState(null);
  const t = useSiteText();

  const load = useCallback(() => {
    api.get(`/tours/${slug}`).then((r) => setTour(r.data)).catch(() => setError(true));
  }, [slug]);
  useEffect(load, [load]);

  useEffect(() => {
    if (!tour) return;
    document.title = `${tour.title} – Geführte Tour | AdventureCouple.lu`;
    const hash = window.location.hash.slice(1);
    if (hash.startsWith("termin-")) setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
  }, [tour]);

  if (error) {
    return (
      <Section>
        <p className="opacity-70" data-testid="tour-not-found">Diese Tour gibt es nicht (mehr).</p>
        <Link to="/tours" className="btn-secondary mt-4 inline-flex">Alle Touren</Link>
      </Section>
    );
  }
  if (!tour) return <Section><Spinner /></Section>;

  const dates = tour.dates || [];
  const jsonLd = eventJsonLd(tour);

  return (
    <div data-testid="tour-detail-page">
      {jsonLd.length > 0 && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />}
      <Section className="!pb-8">
        <Link to="/tours" className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100 mb-6" data-testid="tour-back">
          <ChevronLeft className="w-4 h-4" /> Alle Touren
        </Link>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="rounded-2xl overflow-hidden aspect-[4/3]">
            <img src={tour.image_url} alt={tour.image_alt || tour.title} className="w-full h-full object-cover" data-testid="tour-detail-image" />
          </div>
          <div>
            <div className="overline mb-3">Geführte Tour</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] mb-5" data-testid="tour-detail-title">{tour.title}</h1>
            <TourBadges tour={tour} testId="tour-detail-badges" />
            <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--wp-text-muted)" }} data-testid="tour-detail-description">
              {tour.description || tour.short_description}
            </p>
            <p className="mt-5 text-sm flex items-start gap-2" style={{ color: "var(--wp-olive)" }} data-testid="tour-herb-note">
              <Leaf className="w-4 h-4 mt-0.5 shrink-0" /> {t("tour_herb_note")}
            </p>
            <ShareTour tour={tour} />
          </div>
        </div>
      </Section>

      <Section className="!pt-0 !pb-8">
        <h2 className="font-display text-3xl mb-5">Termine</h2>
        {dates.length === 0 ? (
          <div className="plant-card p-6" data-testid="tour-no-dates">
            <p className="text-sm md:text-base mb-4">{t("tour_no_dates_text")}</p>
            <div className="max-w-md"><NewsletterForm buttonLabel="Eintragen" /></div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="tour-dates-list">
            {dates.map((d) => <TourDateRow key={d.id} d={d} onBook={(x) => setBooking({ ...x, title: tour.title })} />)}
          </div>
        )}
      </Section>

      <Section className="!pt-0">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <GoodToKnow />
          {(tour.gallery || []).length > 0 && (
            <div className={`grid gap-4 ${tour.gallery.length > 1 ? "grid-cols-2" : "grid-cols-1"}`} data-testid="tour-gallery">
              {tour.gallery.map((g) => (
                <figure key={g.url}>
                  <img src={g.url} alt={g.alt || tour.title} loading="lazy" className="w-full aspect-[4/5] object-cover rounded-2xl" />
                  {g.alt && <figcaption className="text-xs opacity-70 mt-2">{g.alt}</figcaption>}
                </figure>
              ))}
            </div>
          )}
        </div>
      </Section>

      {booking && <BookingModal date={booking} onClose={() => setBooking(null)} onBooked={load} />}
    </div>
  );
}
